import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { 
  TrendingUp, TrendingDown, Search, SlidersHorizontal, RotateCcw, 
  ChevronDown, ChevronUp, Zap, Clock, Target, Percent, DollarSign,
  BarChart3, Filter, ArrowUpDown, Sparkles, AlertTriangle, Menu
} from 'lucide-react';
import { fetchApi, formatPrice, formatPercent, formatNumber, getChangeColor, cn } from '../lib/utils';
import { useAuth } from '../context/AuthContext';

const PRESET_FILTERS = [
  {
    id: 'high-probability',
    name: 'High Probability Gains',
    icon: Sparkles,
    description: '80%+ chance to rise',
    filters: { min_rise_probability: 80, sort_by: 'rise_probability', sort_order: 'desc' }
  },
  {
    id: 'top-opportunities',
    name: 'Top Opportunities',
    icon: Target,
    description: 'High confidence bullish',
    filters: { min_confidence: 65, prediction_direction: 'bullish', sort_by: 'predicted_change', sort_order: 'desc' }
  },
  {
    id: 'freshest',
    name: 'Just Updated',
    icon: Clock,
    description: 'Last hour predictions',
    filters: { max_prediction_age_hours: 1, sort_by: 'prediction_age', sort_order: 'asc' }
  },
  {
    id: 'big-movers-up',
    name: 'Big Movers Up',
    icon: TrendingUp,
    description: 'Predicted >5% gain',
    filters: { min_predicted_change: 5, sort_by: 'predicted_change', sort_order: 'desc' }
  },
  {
    id: 'big-movers-down',
    name: 'Big Movers Down',
    icon: TrendingDown,
    description: 'Predicted >5% drop',
    filters: { max_predicted_change: -5, sort_by: 'predicted_change', sort_order: 'asc' }
  },
  {
    id: 'high-confidence',
    name: 'High Confidence',
    icon: Percent,
    description: '70%+ confidence',
    filters: { min_confidence: 70, sort_by: 'confidence', sort_order: 'desc' }
  }
];

export default function AdvancedSearchPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [filterOptions, setFilterOptions] = useState({ sectors: [], countries: [] });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    // Prediction filters
    min_predicted_change: '',
    max_predicted_change: '',
    min_confidence: '',
    max_confidence: '',
    min_rise_probability: '',
    min_fall_probability: '',
    prediction_direction: 'all',
    time_horizon: 'all',
    // Freshness
    max_prediction_age_hours: '',
    min_prediction_age_hours: '',
    // Stock filters
    sector: 'all',
    country: 'all',
    min_market_cap: '',
    max_market_cap: '',
    min_price: '',
    max_price: '',
    min_volume: '',
    min_24h_change: '',
    max_24h_change: '',
    // Sorting
    sort_by: 'confidence',
    sort_order: 'desc',
    // Pagination
    limit: 50,
    offset: 0
  });

  const loadFilterOptions = useCallback(async () => {
    try {
      const data = await fetchApi('/filters');
      setFilterOptions(data);
    } catch (error) {
      console.error('Failed to load filters:', error);
    }
  }, []);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  const buildQueryString = useCallback(() => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '' && value !== 'all' && value !== null && value !== undefined) {
        params.set(key, value.toString());
      }
    });
    
    return params.toString();
  }, [filters]);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const queryString = buildQueryString();
      const data = await fetchApi(`/stocks/advanced-search?${queryString}`);
      setResults(data.stocks || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  }, [buildQueryString]);

  const applyPreset = (preset) => {
    setFilters(prev => ({
      ...prev,
      // Reset relevant filters
      min_predicted_change: '',
      max_predicted_change: '',
      min_confidence: '',
      max_confidence: '',
      min_rise_probability: '',
      min_fall_probability: '',
      prediction_direction: 'all',
      max_prediction_age_hours: '',
      min_prediction_age_hours: '',
      // Apply preset
      ...preset.filters,
      prediction_direction: preset.filters.prediction_direction || 'all'
    }));
  };

  const resetFilters = () => {
    setFilters({
      min_predicted_change: '',
      max_predicted_change: '',
      min_confidence: '',
      max_confidence: '',
      min_rise_probability: '',
      min_fall_probability: '',
      prediction_direction: 'all',
      time_horizon: 'all',
      max_prediction_age_hours: '',
      min_prediction_age_hours: '',
      sector: 'all',
      country: 'all',
      min_market_cap: '',
      max_market_cap: '',
      min_price: '',
      max_price: '',
      min_volume: '',
      min_24h_change: '',
      max_24h_change: '',
      sort_by: 'confidence',
      sort_order: 'desc',
      limit: 50,
      offset: 0
    });
  };

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-sm bg-primary flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold text-lg sm:text-xl">MarketMind</span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            <Link to="/stocks" className="text-muted-foreground hover:text-foreground transition-colors">
              Stocks
            </Link>
            <Link to="/forum" className="text-muted-foreground hover:text-foreground transition-colors">
              Forum
            </Link>
            {isAuthenticated ? (
              <Button onClick={() => navigate('/dashboard')}>Dashboard</Button>
            ) : (
              <Button onClick={() => navigate('/auth')}>Sign In</Button>
            )}
          </div>

          {/* Mobile Menu */}
          <div className="md:hidden">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <SheetHeader>
                  <SheetTitle>Menu</SheetTitle>
                </SheetHeader>
                <div className="flex flex-col gap-4 mt-6">
                  <Link to="/stocks" onClick={() => setMobileMenuOpen(false)} className="py-2 hover:text-primary transition-colors">
                    Stocks
                  </Link>
                  <Link to="/forum" onClick={() => setMobileMenuOpen(false)} className="py-2 hover:text-primary transition-colors">
                    Forum
                  </Link>
                  {isAuthenticated ? (
                    <Button onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }} className="w-full">
                      Dashboard
                    </Button>
                  ) : (
                    <Button onClick={() => { navigate('/auth'); setMobileMenuOpen(false); }} className="w-full">
                      Sign In
                    </Button>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <SlidersHorizontal className="w-8 h-8 text-primary" />
            <h1 className="font-heading font-bold text-3xl md:text-4xl tracking-tight">
              Advanced Search
            </h1>
          </div>
          <p className="text-muted-foreground">
            Find stocks matching your exact criteria with powerful filtering options
          </p>
        </div>

        {/* Quick Presets */}
        <div className="mb-6">
          <p className="text-sm text-muted-foreground mb-3">Quick Filters</p>
          <div className="flex flex-wrap gap-2">
            {PRESET_FILTERS.map((preset) => (
              <Button
                key={preset.id}
                variant="outline"
                size="sm"
                onClick={() => applyPreset(preset)}
                className="gap-2"
                data-testid={`preset-${preset.id}`}
              >
                <preset.icon className="w-4 h-4" />
                {preset.name}
              </Button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Filters Panel */}
          <div className="lg:col-span-1">
            <Card className="bg-card border-border/50 sticky top-24">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Filter className="w-5 h-5" />
                    Filters
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={resetFilters}>
                    <RotateCcw className="w-4 h-4 mr-1" />
                    Reset
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6 max-h-[70vh] overflow-y-auto">
                
                {/* Prediction Filters */}
                <Collapsible defaultOpen>
                  <CollapsibleTrigger className="flex items-center justify-between w-full text-sm font-medium">
                    <span className="flex items-center gap-2">
                      <Target className="w-4 h-4 text-primary" />
                      Prediction Filters
                    </span>
                    <ChevronDown className="w-4 h-4" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3 space-y-4">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Min Change %</Label>
                        <Input
                          type="number"
                          placeholder="-10"
                          value={filters.min_predicted_change}
                          onChange={(e) => updateFilter('min_predicted_change', e.target.value)}
                          className="h-8"
                          data-testid="filter-min-change"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Max Change %</Label>
                        <Input
                          type="number"
                          placeholder="10"
                          value={filters.max_predicted_change}
                          onChange={(e) => updateFilter('max_predicted_change', e.target.value)}
                          className="h-8"
                          data-testid="filter-max-change"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Min Confidence %</Label>
                        <Input
                          type="number"
                          placeholder="50"
                          value={filters.min_confidence}
                          onChange={(e) => updateFilter('min_confidence', e.target.value)}
                          className="h-8"
                          data-testid="filter-min-confidence"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Max Confidence %</Label>
                        <Input
                          type="number"
                          placeholder="100"
                          value={filters.max_confidence}
                          onChange={(e) => updateFilter('max_confidence', e.target.value)}
                          className="h-8"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs">Min Rise Probability %</Label>
                      <Input
                        type="number"
                        placeholder="80"
                        value={filters.min_rise_probability}
                        onChange={(e) => updateFilter('min_rise_probability', e.target.value)}
                        className="h-8"
                        data-testid="filter-min-rise-prob"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Combined chance of any gain</p>
                    </div>

                    <div>
                      <Label className="text-xs">Min Fall Probability %</Label>
                      <Input
                        type="number"
                        placeholder="80"
                        value={filters.min_fall_probability}
                        onChange={(e) => updateFilter('min_fall_probability', e.target.value)}
                        className="h-8"
                      />
                    </div>

                    <div>
                      <Label className="text-xs">Direction</Label>
                      <Select 
                        value={filters.prediction_direction} 
                        onValueChange={(v) => updateFilter('prediction_direction', v)}
                      >
                        <SelectTrigger className="h-8" data-testid="filter-direction">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Any Direction</SelectItem>
                          <SelectItem value="bullish">Bullish Only</SelectItem>
                          <SelectItem value="bearish">Bearish Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-xs">Time Horizon</Label>
                      <Select 
                        value={filters.time_horizon} 
                        onValueChange={(v) => updateFilter('time_horizon', v)}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Any Horizon</SelectItem>
                          <SelectItem value="hours">Hours</SelectItem>
                          <SelectItem value="days">Days</SelectItem>
                          <SelectItem value="weeks">Weeks</SelectItem>
                          <SelectItem value="months">Months</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                {/* Freshness Filters */}
                <Collapsible defaultOpen>
                  <CollapsibleTrigger className="flex items-center justify-between w-full text-sm font-medium">
                    <span className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-primary" />
                      Freshness
                    </span>
                    <ChevronDown className="w-4 h-4" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3 space-y-4">
                    <div>
                      <Label className="text-xs">Max Age (hours)</Label>
                      <Input
                        type="number"
                        placeholder="e.g., 1 for last hour"
                        value={filters.max_prediction_age_hours}
                        onChange={(e) => updateFilter('max_prediction_age_hours', e.target.value)}
                        className="h-8"
                        data-testid="filter-max-age"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Min Age (hours)</Label>
                      <Input
                        type="number"
                        placeholder="e.g., 4 for stale"
                        value={filters.min_prediction_age_hours}
                        onChange={(e) => updateFilter('min_prediction_age_hours', e.target.value)}
                        className="h-8"
                      />
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                {/* Stock Filters */}
                <Collapsible>
                  <CollapsibleTrigger className="flex items-center justify-between w-full text-sm font-medium">
                    <span className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-primary" />
                      Stock Filters
                    </span>
                    <ChevronDown className="w-4 h-4" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3 space-y-4">
                    <div>
                      <Label className="text-xs">Sector</Label>
                      <Select 
                        value={filters.sector} 
                        onValueChange={(v) => updateFilter('sector', v)}
                      >
                        <SelectTrigger className="h-8" data-testid="filter-sector">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Sectors</SelectItem>
                          {filterOptions.sectors.map(s => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-xs">Country</Label>
                      <Select 
                        value={filters.country} 
                        onValueChange={(v) => updateFilter('country', v)}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Countries</SelectItem>
                          {filterOptions.countries.map(c => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Min Price $</Label>
                        <Input
                          type="number"
                          placeholder="0"
                          value={filters.min_price}
                          onChange={(e) => updateFilter('min_price', e.target.value)}
                          className="h-8"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Max Price $</Label>
                        <Input
                          type="number"
                          placeholder="1000"
                          value={filters.max_price}
                          onChange={(e) => updateFilter('max_price', e.target.value)}
                          className="h-8"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Min Cap $</Label>
                        <Input
                          type="number"
                          placeholder="1B"
                          value={filters.min_market_cap}
                          onChange={(e) => updateFilter('min_market_cap', e.target.value)}
                          className="h-8"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Max Cap $</Label>
                        <Input
                          type="number"
                          placeholder="1T"
                          value={filters.max_market_cap}
                          onChange={(e) => updateFilter('max_market_cap', e.target.value)}
                          className="h-8"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Min 24h %</Label>
                        <Input
                          type="number"
                          placeholder="-5"
                          value={filters.min_24h_change}
                          onChange={(e) => updateFilter('min_24h_change', e.target.value)}
                          className="h-8"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Max 24h %</Label>
                        <Input
                          type="number"
                          placeholder="5"
                          value={filters.max_24h_change}
                          onChange={(e) => updateFilter('max_24h_change', e.target.value)}
                          className="h-8"
                        />
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                {/* Sorting */}
                <Collapsible defaultOpen>
                  <CollapsibleTrigger className="flex items-center justify-between w-full text-sm font-medium">
                    <span className="flex items-center gap-2">
                      <ArrowUpDown className="w-4 h-4 text-primary" />
                      Sorting
                    </span>
                    <ChevronDown className="w-4 h-4" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3 space-y-4">
                    <div>
                      <Label className="text-xs">Sort By</Label>
                      <Select 
                        value={filters.sort_by} 
                        onValueChange={(v) => updateFilter('sort_by', v)}
                      >
                        <SelectTrigger className="h-8" data-testid="filter-sort-by">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="confidence">Confidence</SelectItem>
                          <SelectItem value="predicted_change">Predicted Change</SelectItem>
                          <SelectItem value="rise_probability">Rise Probability</SelectItem>
                          <SelectItem value="fall_probability">Fall Probability</SelectItem>
                          <SelectItem value="prediction_age">Freshness</SelectItem>
                          <SelectItem value="market_cap">Market Cap</SelectItem>
                          <SelectItem value="price">Price</SelectItem>
                          <SelectItem value="24h_change">24h Change</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Order</Label>
                      <Select 
                        value={filters.sort_order} 
                        onValueChange={(v) => updateFilter('sort_order', v)}
                      >
                        <SelectTrigger className="h-8" data-testid="filter-sort-order">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="desc">Descending (High to Low)</SelectItem>
                          <SelectItem value="asc">Ascending (Low to High)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                {/* Search Button */}
                <Button 
                  className="w-full glow-primary" 
                  onClick={search}
                  disabled={loading}
                  data-testid="search-btn"
                >
                  <Search className="w-4 h-4 mr-2" />
                  {loading ? 'Searching...' : 'Search'}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Results Panel */}
          <div className="lg:col-span-3">
            <Card className="bg-card border-border/50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Results</CardTitle>
                    <CardDescription>
                      {total > 0 ? `Found ${total} matching stocks` : 'Apply filters and click Search'}
                    </CardDescription>
                  </div>
                  {results.length > 0 && (
                    <Badge variant="outline" className="font-mono">
                      {total} stocks
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Skeleton key={i} className="h-20 w-full" />
                    ))}
                  </div>
                ) : results.length === 0 ? (
                  <div className="text-center py-12">
                    <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      No results yet. Configure filters and click Search.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {results.map((stock) => (
                      <div
                        key={stock.stock_id || stock.ticker}
                        className="p-4 border border-border/50 rounded-sm hover:border-primary/50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/stocks/${stock.ticker}`)}
                        data-testid={`result-${stock.ticker}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-mono font-bold text-lg">{stock.ticker}</span>
                                {stock.sector && (
                                  <Badge variant="outline" className="text-xs">{stock.sector}</Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">{stock.name}</p>
                            </div>
                          </div>

                          <div className="flex items-center gap-6">
                            {/* Price */}
                            <div className="text-right">
                              <p className="font-mono font-medium">{formatPrice(stock.current_price)}</p>
                              <p className={cn("text-sm font-mono", getChangeColor(stock.price_change_pct_24h))}>
                                {formatPercent(stock.price_change_pct_24h)} 24h
                              </p>
                            </div>

                            {/* Prediction */}
                            {stock.prediction ? (
                              <div className="text-right min-w-[120px]">
                                <p className={cn("font-mono font-bold text-lg", getChangeColor(stock.prediction.predicted_change_pct))}>
                                  {formatPercent(stock.prediction.predicted_change_pct)}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {stock.prediction.confidence}% conf
                                </p>
                              </div>
                            ) : (
                              <div className="text-right min-w-[120px]">
                                <p className="text-muted-foreground">-</p>
                              </div>
                            )}

                            {/* Probabilities */}
                            {stock.rise_probability !== null && (
                              <div className="text-right min-w-[100px]">
                                <div className="flex items-center gap-1 justify-end">
                                  <TrendingUp className="w-3 h-3 text-green-500" />
                                  <span className="text-sm font-mono text-green-500">{stock.rise_probability}%</span>
                                </div>
                                <div className="flex items-center gap-1 justify-end">
                                  <TrendingDown className="w-3 h-3 text-red-500" />
                                  <span className="text-sm font-mono text-red-500">{stock.fall_probability}%</span>
                                </div>
                              </div>
                            )}

                            {/* Freshness */}
                            {stock.prediction_age_hours !== null && (
                              <div className="text-right min-w-[60px]">
                                <Badge 
                                  variant="outline" 
                                  className={cn(
                                    "text-xs",
                                    stock.prediction_age_hours < 1 && "border-green-500 text-green-500",
                                    stock.prediction_age_hours >= 1 && stock.prediction_age_hours < 3 && "border-yellow-500 text-yellow-500",
                                    stock.prediction_age_hours >= 3 && "border-muted-foreground"
                                  )}
                                >
                                  {stock.prediction_age_hours < 1 
                                    ? `${Math.round(stock.prediction_age_hours * 60)}m ago`
                                    : `${stock.prediction_age_hours.toFixed(1)}h ago`
                                  }
                                </Badge>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
